/* eslint-disable max-lines */
import { v4 } from 'uuid'

import {
  ArrayFieldModel,
  CheckboxFieldModel,
  DateFieldModel,
  FieldType,
  FileFieldModel,
  FixedPictureFieldModel,
  FixedTextFieldModel,
  FormField,
  FormFieldInput,
  FormModelStructureInput,
  FormModelValidationConfigInput,
  GridTextFieldModel,
  HeaderPictureFieldModel,
  PictureFieldModel,
  RadioFieldModel,
  SectionFieldModel,
  SelectEditableFieldModel,
  SelectFieldModel,
  SignatureFieldModel,
  TextFieldModel,
} from '@types'

type FieldTypeConfig = {
  key: keyof FormFieldInput
  isArray?: boolean
  hasValue?: boolean
  hasRequiredValue?: boolean
  hasValues?: boolean
  hasFiles?: boolean
  hasFile?: boolean
  hasOptions?: boolean
  baseOnly?: boolean
  isGrid?: boolean
}

type FieldConfigs = {
  [K in FieldType]: FieldTypeConfig
}

type FormFieldOptionsType = CheckboxFieldModel | RadioFieldModel | SelectFieldModel | SelectEditableFieldModel

export function transformFormFieldToInput(field: FormField): FormFieldInput {
  const baseProperties = {
    id: field.id,
    ...('required' in field && { required: field.required }),
    ...('label' in field && { label: field.label }),
  }

  const fieldTypeConfigs: FieldConfigs = {
    [FieldType.HeaderPicture]: { key: 'header_picture', hasFile: true },
    [FieldType.Section]: { key: 'section', baseOnly: true },
    [FieldType.Title]: { key: 'title', baseOnly: true },
    [FieldType.Array]: { key: 'array', isArray: true },
    [FieldType.Text]: { key: 'text', hasValue: true },
    [FieldType.Date]: { key: 'date', hasValue: true },
    [FieldType.Signature]: { key: 'signature', hasValue: true },
    [FieldType.File]: { key: 'file', hasFiles: true },
    [FieldType.Picture]: { key: 'picture', hasFiles: true },
    [FieldType.Radio]: { key: 'radio', hasValue: true, hasOptions: true },
    [FieldType.Checkbox]: { key: 'checkbox', hasValues: true, hasOptions: true },
    [FieldType.Select]: { key: 'select', hasValue: true, hasOptions: true },
    [FieldType.SelectEditable]: { key: 'select_editable', hasValue: true, hasOptions: true },
    [FieldType.FixedText]: { key: 'fixed_text', hasRequiredValue: true },
    [FieldType.FixedPicture]: { key: 'fixed_picture', hasFiles: true },
    [FieldType.FixedFile]: { key: 'fixed_file', hasFiles: true },
    [FieldType.GridText]: { key: 'grid_text', isGrid: true },
  }

  const config = fieldTypeConfigs[field.type]
  if (!config) {
    throw new Error(`Unsupported field type: ${field.type}`)
  }

  const fieldProperties = {
    ...baseProperties,
    ...('hasValue' in config && 'value' in field && { value: field.value }),
    ...('hasValues' in config && 'values' in field && { values: field.values }),
    ...('hasRequiredValue' in config && 'requiredValue' in field && { requiredValue: field.requiredValue }),
    ...('hasFiles' in config && 'localFiles' in field && { localFiles: field.localFiles }),
    ...('hasFiles' in config && 'ids' in field && { ids: field.ids }),
    ...('hasFiles' in config && 'fileIds' in field && { fileIds: field.fileIds }),
    ...('hasFile' in config && 'fileId' in field && { fileId: field.fileId }),
    ...('isArray' in config &&
      'cols' in field && {
        cols: field.cols.map((col) => ({ id: col.id, fields: col.fields.map(transformFormFieldToInput) })),
      }),
    ...('isGrid' in config &&
      'rows' in field && {
        rows: field.rows.map((row) => ({
          id: row.id,
          label: row.label,
          columns: row.columns.map((column) => ({
            id: column.id,
            label: column.label,
            required: column.required,
            value: column.value,
          })),
        })),
      }),

    ...('hasOptions' in config &&
      'options' in field && {
        options: field.options.map((option) => ({
          id: option.id,
          label: option.label,
          redirectTo: option.redirectTo,
        })),
      }),
  }

  return {
    type: field.type,
    [config.key]: config.baseOnly ? baseProperties : fieldProperties,
  }
}

export function transformFormModelStructureToInput(
  fields: FormField[],
  validationConfig: FormModelValidationConfigInput,
): FormModelStructureInput {
  return {
    fields: fields.map(transformFormFieldToInput),
    validationConfig: {
      mode: validationConfig.mode,
      allowAdminEdit: validationConfig.allowAdminEdit,
    },
  }
}

const isNonRequiredField = (type: FieldType): boolean => {
  const nonRequiredFields = [
    FieldType.Title,
    FieldType.Section,
    FieldType.FixedText,
    FieldType.FixedPicture,
    FieldType.FixedFile,
    FieldType.GridText,
  ]
  return nonRequiredFields.includes(type)
}

export const isFieldWithOptions = (type: FieldType): boolean => {
  const fieldsWithOptions = [FieldType.Checkbox, FieldType.Radio, FieldType.Select, FieldType.SelectEditable]
  return fieldsWithOptions.includes(type)
}

export const createBaseFieldFrom = (type: FieldType): FormField => {
  if (type === FieldType.HeaderPicture) {
    return {
      id: v4(),
      type,
    } as HeaderPictureFieldModel
  }

  if (type === FieldType.GridText) {
    return {
      id: v4(),
      type,
      rows: [
        {
          id: v4(),
          label: '',
          columns: [createBaseFieldFrom(FieldType.Text)],
        },
      ],
    } as GridTextFieldModel
  }

  const baseField = {
    id: v4(),
    type,
    label: '',
  }

  const fieldWithRequired = isNonRequiredField(type) ? baseField : { ...baseField, required: false }
  if (type === FieldType.Array) {
    return {
      ...fieldWithRequired,
      cols: [],
    }
  }

  const fieldWithOptions = isFieldWithOptions(type)
    ? {
        ...fieldWithRequired,
        options: [
          { id: v4(), label: '' },
          { id: v4(), label: '' },
        ],
      }
    : fieldWithRequired

  return fieldWithOptions
}

/*
  Generate new IDs for all fields in the form structure
*/
export const generateNewIdsForField = (field: FormField): FormField => {
  // Create base duplicated field with new ID
  const duplicatedField = {
    ...field,
    id: v4(),
  }

  // Handle grid text fields - generate new IDs for all rows, columns and cells
  const isGridTextField = field.type === FieldType.GridText && 'rows' in field
  if (isGridTextField) {
    const gridField = field as GridTextFieldModel
    return {
      ...duplicatedField,
      rows: gridField.rows.map((row) => ({
        ...row,
        id: v4(),
        columns: row.columns.map((column) => ({
          ...column,
          id: v4(),
        })),
      })),
    } as GridTextFieldModel
  }

  // Handle array fields - recursively duplicate all nested fields
  const isArrayField = field.type === FieldType.Array && 'cols' in field
  if (isArrayField) {
    const arrayField = field
    return {
      ...duplicatedField,
      cols: arrayField.cols.map((col) => ({
        id: v4(),
        fields: col.fields.map((nestedField) => generateNewIdsForField(nestedField)),
      })),
    } as ArrayFieldModel
  }

  // Handle fields with options - generate new IDs for all options
  const isOptionsField = isFieldWithOptions(field.type) && 'options' in field
  if (isOptionsField) {
    const fieldWithOptions = field as FormFieldOptionsType
    return {
      ...duplicatedField,
      options: fieldWithOptions.options.map((option) => ({
        ...option,
        id: v4(),
      })),
    } as FormFieldOptionsType
  }

  return duplicatedField
}

/*
  Reset field values to default values
 */
export const resetFieldValues = (field: FormField, generateNewId: boolean = true): FormField => {
  // Create base field with potentially new ID
  const baseField = {
    ...field,
    ...(generateNewId && { id: v4() }),
  }

  // Handle grid text fields
  const isGridTextField = field.type === FieldType.GridText && 'rows' in field
  if (isGridTextField) {
    const gridField = field as GridTextFieldModel
    return {
      ...baseField,
      rows: gridField.rows.map((row) => ({
        ...row,
        ...(generateNewId && { id: v4() }),
        columns: row.columns.map((column) => ({
          ...column,
          ...(generateNewId && { id: v4() }),
          value: undefined,
        })),
      })),
    } as GridTextFieldModel
  }

  // Handle array fields recursively
  const isArrayField = field.type === FieldType.Array && 'cols' in field
  if (isArrayField) {
    return {
      ...baseField,
      cols: field.cols.map((col) => ({
        ...col,
        fields: col.fields.map((nestedField) => resetFieldValues(nestedField, generateNewId)),
      })),
    } as ArrayFieldModel
  }

  // Handle fields with options - preserve options but generate new IDs if requested
  const isOptionsField = isFieldWithOptions(field.type) && 'options' in field
  if (isOptionsField) {
    return {
      ...baseField,
      ...(generateNewId && {
        options: field.options.map((option) => ({
          ...option,
          id: v4(),
        })),
      }),
      // Reset value/values
      ...('value' in field && { value: undefined }),
      ...('values' in field && { values: undefined }),
    } as FormFieldOptionsType
  }

  // Handle fields with value
  if ('value' in field) {
    return {
      ...baseField,
      value: undefined,
    } as TextFieldModel | DateFieldModel | SignatureFieldModel
  }

  // Handle fields with values (like checkbox)
  if ('values' in field) {
    return {
      ...baseField,
      values: undefined,
    } as CheckboxFieldModel
  }

  // Handle fields with files
  if ('fileIds' in field) {
    return {
      ...baseField,
      fileIds: undefined,
    } as FileFieldModel | PictureFieldModel
  }

  // Fixed fields (fixed_text, fixed_picture, fixed_file, header_picture) and sections don't need value reset
  return baseField as
    | FixedTextFieldModel
    | FixedPictureFieldModel
    | FixedTextFieldModel
    | SectionFieldModel
    | HeaderPictureFieldModel
}

export interface FlattenedField {
  fieldId: string
  fieldType: string
  label: string
  required?: boolean
  value?: string | string[]
  options?: string
}

export const flattenFormField = (field: FormField): FlattenedField => {
  const baseField = {
    fieldId: field.id,
    fieldType: field.type,
    label: 'label' in field ? field.label : '',
    required: 'required' in field ? field.required : false,
  }

  switch (field.type) {
    case FieldType.Signature:
      return {
        ...baseField,
        value: 'value' in field ? field.value : undefined,
      }
    case FieldType.Text:
    case FieldType.Date:
      return {
        ...baseField,
        value: 'value' in field ? field.value : undefined,
      }

    case FieldType.Select:
    case FieldType.SelectEditable:
    case FieldType.Radio:
      return {
        ...baseField,
        value: 'value' in field ? field.value : undefined,
        options: 'options' in field ? field.options?.map((opt) => opt.label).join(', ') : undefined,
      }

    case FieldType.Checkbox:
      return {
        ...baseField,
        value: 'values' in field ? field.values : undefined,
        options: 'options' in field ? field.options?.map((opt) => opt.label).join(', ') : undefined,
      }

    case FieldType.File:
    case FieldType.Picture:
      return {
        ...baseField,
        value: 'fileIds' in field ? field.fileIds?.join(', ') : undefined,
      }

    case FieldType.Array:
      if ('cols' in field) {
        const arrayFields = field.cols
          .map((col, index) => {
            const colFields = col.fields.map((f) => flattenFormField(f))
            return colFields.map((f) => ({
              ...f,
              fieldId: `${field.id}_col${index}_${f.fieldId}`,
              label: `${field.label} - Column ${index + 1} - ${f.label}`,
            }))
          })
          .flat()
        return {
          ...baseField,
          arrayFields,
        } as any
      }

      return baseField

    case FieldType.GridText:
      if ('rows' in field) {
        const gridField = field as GridTextFieldModel
        const gridFields: FlattenedField[] = []
        for (let rowIndex = 0; rowIndex < gridField.rows.length; rowIndex++) {
          const row = gridField.rows[rowIndex]
          for (let colIndex = 0; colIndex < row.columns.length; colIndex++) {
            const column = row.columns[colIndex]
            gridFields.push({
              fieldId: `${field.id}_row${rowIndex}_col${colIndex}`,
              fieldType: FieldType.GridText,
              label: `${row.label} - ${column.label}`,
              required: column.required,
              value: column.value,
            })
          }
        }
        return { ...baseField, gridFields } as any
      }
      return baseField

    default:
      return baseField
  }
}

export const setRequiredOnArrayFields = (field: ArrayFieldModel, required: boolean): ArrayFieldModel => {
  const newField = { ...field }

  newField.cols = field.cols.map((col) => {
    const newCol = { ...col }

    newCol.fields = col.fields.map((field) => {
      if ('required' in field) {
        return { ...field, required }
      } else if ('rows' in field && field.type === FieldType.GridText) {
        const newNestedField = { ...field }

        newNestedField.rows = field.rows.map((row) => {
          const newRow = { ...row }
          newRow.columns = row.columns.map((column) => {
            if ('required' in column) return { ...column, required }
            return column
          })
          return newRow
        })
        return newNestedField
      }
      return field
    })
    return newCol
  })

  newField.required = required

  return newField
}

export const fieldsAreValid = (fields: FormField[]) => {
  if (fields.length === 0) return false

  const arrayFields = fields.filter((field) => field.type === FieldType.Array) as ArrayFieldModel[]
  if (arrayFields.length > 0) {
    const colsEmpty = arrayFields.some((field) => field.cols.length === 0)
    if (colsEmpty) return false
  }

  const gridTextFields = fields.filter((field) => field.type === FieldType.GridText) as GridTextFieldModel[]
  if (gridTextFields.length > 0) {
    const rowsEmpty = gridTextFields.some((field) => field.rows.length === 0)
    if (rowsEmpty) return false
  }

  return true
}
